import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const benefits = [
  {
    title: 'Enhanced Creativity',
    description: 'Children develop imagination through tactile play and open-ended activities',
    color: 'brand-yellow',
  },
  {
    title: 'Better Focus',
    description: 'Without screen distractions, children develop sustained attention spans',
    color: 'brand-blue',
  },
  {
    title: 'Social Skills',
    description: 'Face-to-face interactions promote emotional intelligence and communication',
    color: 'brand-pink',
  },
  {
    title: 'Physical Development',
    description: 'Hands-on activities strengthen fine and gross motor skills',
    color: 'brand-green',
  },
];

const Approach: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="approach" className="bg-brand-light p-16">
      <div className="max-w-7xl mx-auto p-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-brand-pink mb-8">Our Screen-Free Approach</h2>
          <p className="text-xl text-brand-purple max-w-2xl mx-auto mb-8">
            Why we choose hands-on learning over digital education
          </p>
        </motion.div>
        <div className="mb-16">
          {/* Main Content (Full Width) */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-brand-blue mb-8">The Science Behind Screen-Free Learning</h3>
            <p className="text-lg text-brand-dark mb-8 leading-relaxed">
              The American Academy of Pediatrics recommends limiting screen time for 3-year-olds to about 1 hour per day of high-quality content. Since children already receive screen time at home, we fill the educational gap with engaging, hands-on activities.
            </p>
            <div className="grid sm:grid-cols-2 gap-12">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: 0.4 + idx * 0.1, ease: 'easeOut' }}
                  className={`bg-${benefit.color} bg-opacity-10 border-l-8 border-${benefit.color} rounded-2xl py-6 px-16 text-brand-dark shadow-bubble`}
                >
                  <h4 className={`font-bold text-lg mb-1 text-${benefit.color}`}>
                    {benefit.title}
                  </h4>
                  <p className="text-base text-brand-dark">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Approach; 